import React, { Fragment, ReactNode, useState } from 'react';
import { useEffect, useRef } from 'react';
import './plongeon.css';
import { PrecisionBar, PrecisionBarHandle } from './components/precision-bar';
import Plongeur, { ScoreDetail } from './game-engine/plongeur';
import { Button } from '../shared/button/button';
import { GameComponentArgs, GameEnum } from '../../App';
import {ScoreDetailsComponent} from './components/score-details';
import { GlobalScoreComponent } from './components/global-score';

export class GameState {
  heightForce: number | null;
  rotationSpeed: number | null;
  plongeur: Plongeur = new Plongeur(this);

  constructor() {
    this.heightForce = null;
    this.rotationSpeed = null;
  }
}

export enum PlongeurEnum {
  Victor = "Victor",
  Elisa = "Élisa",
  Alice = "Alice",
  Ines = "Inès"
}

export type GlobalScore = {score: number, detail: ScoreDetail, plongeur: PlongeurEnum}[];

export default function Plongeon({setCurrentGame}: GameComponentArgs) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameStateRef = useRef<GameState>(new GameState());
  const rotationBarRef = useRef<PrecisionBarHandle>(null);
  const heightBarRef = useRef<PrecisionBarHandle>(null);
  const interfaceRef = useRef<HTMLDivElement>(null);

  const gameState = gameStateRef.current;

  const [heightForce, setHeightForce] = useState<number | null>(null);
  const [rotationSpeed, setRotationSpeed] = useState<number | null>(null);
  const [score, setScore] = useState<{score: number, detail: ScoreDetail} | null>(null);
  const [turn, setTurn] = useState<PlongeurEnum>(PlongeurEnum.Victor);
  const [nextTurn, setNextTurn] = useState<PlongeurEnum | null>(null);
  const [scoreAreDisplayed, setScoreAreDisplayed] = useState<boolean>(false);

  const [globalScore, setGlobalScore] = useState<GlobalScore>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas resolution to match its display size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const zoom = canvas.height / 400;

    // Disable image smoothing for pixelated rendering
    ctx.imageSmoothingEnabled = false;

    // Load background image
    const backgroundImg = new Image();
    backgroundImg.src = '/plongeon.png';

    let lastTime = Date.now();

    // Animation loop
    const animate = () => {
      const now = Date.now();
      const deltaTime = now - lastTime;
      lastTime = now;


      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw background
      if (backgroundImg.complete) {
        ctx.drawImage(backgroundImg, 0, 0, zoom * backgroundImg.width, zoom * backgroundImg.height);
      }

      gameState.plongeur.draw(deltaTime, ctx, zoom);
      
      if (backgroundImg.complete) {
        ctx.drawImage(
            backgroundImg,
            170, 290, // source x, y
            330, 110, // source width, height
            zoom * 170, zoom * 290, // destination x, y (centered)
            zoom * 330, zoom * 110 // destination width, height
        );
      }


      requestAnimationFrame(animate);
    };

    // Start animation when both images are loaded
    Promise.all([
      new Promise(resolve => backgroundImg.onload = resolve),
      new Promise(resolve => gameState.plongeur.spriteImg.onload = resolve)
    ]).then(() => {
      animate();
    });

    setTimeout(() => heightBarRef.current?.focus(), 10);
  }, [gameState.plongeur]);

  useEffect(() => {
    setNextTurn(getNextTurn(globalScore));
  }, [globalScore, turn]);

  const getNextTurn = (globalScore: GlobalScore) => {
    const remaining = [PlongeurEnum.Alice, PlongeurEnum.Elisa, PlongeurEnum.Ines, PlongeurEnum.Victor]
      .filter((plongeur) => !globalScore.find((g) => g.plongeur === plongeur));

    if (remaining.length === 0) {
      return null
    }
    return remaining[Math.floor(Math.random() * remaining.length)];
  }

  const goToHome = () => {
    setCurrentGame(GameEnum.Home);
  }

  const onHeightSelected = (height: number) => {
    gameState.heightForce = height;
    setHeightForce(height);
    gameState.plongeur.goIdle(height);
    // Focus on the rotation bar after selecting height
    setTimeout(() => rotationBarRef.current?.focus(), 10);
  }

  const onRotationSelected = (rotation: number) => {
    gameState.rotationSpeed = rotation;
    setRotationSpeed(rotation);
    // Focus on the interface after selecting rotation force
    setTimeout(() => interfaceRef.current?.focus(), 10);
  }

  const onInterfaceClick = () => {
    if (rotationSpeed === null || scoreAreDisplayed) return;
    if (score) retry();
    gameState.plongeur.jump(onJumpFinished);
  }

  const onJumpFinished = () => {
    const scoreData = {
      score: gameState.plongeur.getScore(),
      detail: gameState.plongeur.score as ScoreDetail, 
      plongeur: turn
    };
    setScore(scoreData);
    setGlobalScore([...globalScore, scoreData]);
  }

  const retry = () => {
    if (!nextTurn && !scoreAreDisplayed) {
      setScoreAreDisplayed(true);
      return;
    }

    if (nextTurn) {
      setTurn(nextTurn);
    } else {
      setGlobalScore([]);
      setScoreAreDisplayed(false);
      setTurn(getNextTurn([]) as PlongeurEnum);
    }
    setScore(null);
    setRotationSpeed(null);
    setHeightForce(null);
    gameStateRef.current = new GameState();
    setTimeout(() => heightBarRef.current?.focus(), 10);
  }

  const rotationBar = gameStateRef.current.heightForce === null ? "" : <PrecisionBar
    ref={rotationBarRef}
    className="rotation-bar"
    direction='horizontal'
    onStop={onRotationSelected}
    focus={rotationSpeed === null}
  />

  const precisionBars = score ? undefined : <Fragment>
    <PrecisionBar ref={heightBarRef} className="height-bar" direction="vertical" onStop={onHeightSelected} focus={heightForce === null}/>
    {rotationBar}
  </Fragment>;

  let titleElement;
  let scoreOrConsigne;
  let scoreDetailsElement;

  if (!scoreAreDisplayed) {
    switch (turn) {
      case PlongeurEnum.Victor:
        titleElement = <div className='title'>C'est au tour de Victor !</div>
        break;
      case PlongeurEnum.Elisa:
        titleElement = <div className='title'>C'est au tour d'Élisa !</div>
        break;
      case PlongeurEnum.Alice:
        titleElement = <div className='title'>C'est au tour d'Alice !</div>
        break;
      case PlongeurEnum.Ines:
        titleElement = <div className='title'>C'est au tour d'Inès !</div>
        break;
    };

    let consigne = "Appuie sur n'importe quelle touche pour sauter le plus haut possible !";
    if (heightForce !== null) consigne = "Appuie sur n'importe quelle touche pour faire un maximum de saltos !";
    if (rotationSpeed !== null) consigne = "Saute quand tu es prêt, et choisis le bon moment pour plonger pour éviter de faire un plat !";

    let scoreElement;
    if (score && !scoreAreDisplayed) {
      scoreDetailsElement = <ScoreDetailsComponent scoreDetails={score.detail} />;

      const globalScoreNumber = globalScore.reduce((total, plongeurScore) => total + plongeurScore.score, 0);
      scoreElement = <div className="score">
        <div className='score-commentary'>{score.detail.indication}</div>
        <div className='score-number'>{score.score} Points (Score global: {globalScoreNumber}pts)</div>
      </div>;
    }

    scoreOrConsigne = score ? scoreElement : <div className="consigne">{consigne}</div>;
  }

  


  let boutonText = nextTurn ? `Faire sauter ${nextTurn} !` : "Afficher les scores";
  if (scoreAreDisplayed) boutonText = "Sauter encore !";
  const retryButton = score ? <Button className="retry-button" onClick={retry}>{boutonText}</Button> : undefined;

  let centerElements: ReactNode = retryButton;
  if (scoreAreDisplayed) {
    centerElements = <Fragment>
      <GlobalScoreComponent score={globalScore}/>
      {retryButton}
    </Fragment>
  }

  const classes = ["interface"];
  if (rotationSpeed !== null) classes.push("focus");

  return (
    <Fragment>
      <div className="canvas-container">
        <canvas ref={canvasRef} />
        {precisionBars}
      </div>
      <div className={classes.join(" ")} ref={interfaceRef} onKeyDown={onInterfaceClick} onClick={onInterfaceClick} tabIndex={0}>
        <div className='top'>
          <div className="title-container">
            {titleElement}
          </div>
          <div className="top-bar">
            {scoreOrConsigne}
          </div>
        </div>
        <div className='center'>
          {centerElements}
        </div>
        <div className='bottom'>
          <Button className="go-back-button" onClick={goToHome}>Retourner à Cervens</Button>
          {scoreDetailsElement}
        </div>
      </div>
    </Fragment>
  );
}
